import { queryClient } from '@/lib/react-query';
import { GET_CUSTOMER_PROFILE_FROM_CASE_QUERY_KEY } from '@/features/cases/case/api/get-customer-profile-from-case.api';
import { TSingleCase } from '@/features/cases/case/api/get-single-case.api';
import { TBulkAssignLabelCaseValidation } from '../../../api/bulk-assign-label-case.api';
import { TLabelCase } from '../../../types/label-case.type';
import { AxiosErrorCustom } from '@/lib/axios';
import { axiosExtractErrorMessage } from '@/lib/axios';

export type TBulkAssignLabelCaseOptimisticContext = {
  previousDataByCaseId: Record<string, { payload: TSingleCase }>;
};

export interface IBulkAssignLabelCaseOptimisticUpdateParams {
  allAvailableLabels: TLabelCase[];
}

/**
 * Optimistic update handler for bulk assign label case mutation
 * Updates the case profile cache for each case immediately when labels are assigned
 */
export const createBulkAssignLabelCaseOptimisticUpdate = ({
  allAvailableLabels,
}: IBulkAssignLabelCaseOptimisticUpdateParams) => {
  return {
    onMutate: async (variables: TBulkAssignLabelCaseValidation): Promise<TBulkAssignLabelCaseOptimisticContext> => {
      const previousDataByCaseId: Record<string, { payload: TSingleCase }> = {};
      const caseIds = variables.caseIds ?? [];

      // Map labelIds to full label objects (shared across all cases)
      const updatedLabelCases = variables.labelIds
        .map((labelId: string) => {
          const label = allAvailableLabels.find((l: TLabelCase) => l.labelCaseId === labelId);
          return label
            ? {
                labelCaseId: label.labelCaseId,
                name: label.name,
                color: label.color,
              }
            : null;
        })
        .filter((label): label is { labelCaseId: string; name: string; color: string } => label !== null);

      for (const caseId of caseIds) {
        await queryClient.cancelQueries({
          queryKey: [GET_CUSTOMER_PROFILE_FROM_CASE_QUERY_KEY, caseId],
        });

        const previousData = queryClient.getQueryData<{ payload: TSingleCase }>([
          GET_CUSTOMER_PROFILE_FROM_CASE_QUERY_KEY,
          caseId,
        ]);
        if (previousData) {
          previousDataByCaseId[caseId] = previousData;
        }

        if (previousData?.payload) {
          queryClient.setQueryData<{ payload: TSingleCase }>(
            [GET_CUSTOMER_PROFILE_FROM_CASE_QUERY_KEY, caseId],
            (old) => {
              if (!old?.payload) return old;
              return {
                ...old,
                payload: {
                  ...old.payload,
                  labelCases: updatedLabelCases,
                },
              };
            }
          );
        }
      }

      return { previousDataByCaseId };
    },

    onError: (
      error: AxiosErrorCustom,
      _variables: TBulkAssignLabelCaseValidation,
      context: TBulkAssignLabelCaseOptimisticContext | undefined
    ) => {
      if (context?.previousDataByCaseId) {
        for (const [caseId, previousData] of Object.entries(context.previousDataByCaseId)) {
          queryClient.setQueryData([GET_CUSTOMER_PROFILE_FROM_CASE_QUERY_KEY, caseId], previousData);
        }
      }
      return {
        errorMessage: axiosExtractErrorMessage(error) || 'Failed to assign case labels',
      };
    },

    onSuccess: () => {
      // Don't invalidate immediately - let the optimistic update stay visible
    },
  };
};
